import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import api from '../lib/api';
import { setPageMeta, fmtDate, JOURNAL_CATEGORIES } from '../lib/shop';

export default function Journal() {
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => { setPageMeta('Research Journal', 'Educational articles on research compounds, documentation and laboratory handling.'); }, []);

  useEffect(() => {
    const params = {};
    if (category) params.category = category;
    if (q) params.q = q;
    const t = setTimeout(() => {
      api.get('/journal', { params }).then((r) => setArticles(r.data)).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [category, q]);

  const [featured, ...rest] = articles;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="journal-page">
      <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A8F95]">VQ Health</p>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2">Research Journal</h1>

      <div className="flex flex-col sm:flex-row gap-4 sm:items-center mt-8">
        <div className="flex items-center gap-3 border-b-2 border-[#0B0F12] pb-2 flex-1 max-w-md">
          <Search className="w-4 h-4 text-[#8A8F95]" />
          <input data-testid="journal-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…"
            className="flex-1 bg-transparent focus:outline-none placeholder:text-[#B7BCC1]" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button data-testid="journal-cat-all" onClick={() => setCategory('')}
            className={`px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-wider border ${!category ? 'bg-[#0B0F12] text-white border-[#0B0F12]' : 'border-[#D8D8D0]'}`}>All</button>
          {JOURNAL_CATEGORIES.map((c) => (
            <button key={c} data-testid={`journal-cat-${c.toLowerCase().replace(/[^a-z]+/g, '-')}`} onClick={() => setCategory(category === c ? '' : c)}
              className={`px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-wider border ${category === c ? 'bg-[#0B0F12] text-white border-[#0B0F12]' : 'border-[#D8D8D0] hover:border-[#0B0F12]'}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {featured && !q && !category && (
        <Link to={`/journal/${featured.slug}`} data-testid="journal-featured"
          className="block border hairline bg-white mt-10 grid grid-cols-1 md:grid-cols-2 hover:border-[#0B0F12] transition-colors">
          <div className="bg-[#0B0F12] text-[#FAFAF8] p-8 flex flex-col justify-between min-h-[220px]">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#8A8F95]">Featured · {featured.category}</p>
            <p className="font-display font-black uppercase text-3xl leading-tight">{featured.title}</p>
          </div>
          <div className="p-8 flex flex-col justify-center">
            <p className="text-[#575B60] leading-relaxed">{featured.excerpt}</p>
            <p className="font-mono2 text-[10px] uppercase tracking-widest text-[#8A8F95] mt-4">{featured.author} · {fmtDate(featured.publish_at)} · {featured.reading_time} min</p>
          </div>
        </Link>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
        {(q || category ? articles : rest).map((a) => (
          <Link key={a.id} to={`/journal/${a.slug}`} data-testid={`journal-card-${a.slug}`}
            className="border hairline bg-white p-6 hover:border-[#0B0F12] transition-colors flex flex-col group">
            {a.hero_image && <img src={a.hero_image} alt="" className="w-full aspect-[16/9] object-cover mb-4 -mt-2" />}
            <p className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#8A8F95]">{a.category}</p>
            <p className="font-display font-bold uppercase text-xl leading-tight mt-2 group-hover:underline">{a.title}</p>
            <p className="text-sm text-[#575B60] mt-2 line-clamp-3">{a.excerpt}</p>
            <p className="font-mono2 text-[10px] uppercase tracking-widest text-[#8A8F95] mt-auto pt-4">{fmtDate(a.publish_at)} · {a.reading_time} min read</p>
          </Link>
        ))}
      </div>
      {articles.length === 0 && <p className="text-center py-16 text-[#575B60]" data-testid="journal-empty">No articles found.</p>}
    </div>
  );
}
